import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  NgZone,
  output,
  signal,
} from '@angular/core';
import { DsIconModule, DsTabsModule, DsToastService } from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';

import { DealerProvider } from '../../../auth/dealer/dealer.provider';
import { ReadonlyService } from '../../../auth/readonly/readonly.service';
import { ConsentBoxesComponent } from '../../consent/components/consent-boxes/consent-boxes.component';
import { ConsentComposable } from '../../consent/composables/consent.composable';
import { ConsentProvider } from '../../consent/services/consent.provider';
import { GcdmLanguageProvider } from '../../consent/services/gcdm-language.provider';
import { Customer } from '../../customer/model/customer.model';
import { CustomerLoadResult, CustomerLoadService } from '../../customer/services/customer-load.service';
import { CustomerMasterDataSource } from '../../customer/services/customer-source';
import { CustomerUpdateFailureReason, CustomerUpdateService } from '../../customer/services/customer-update.service';
import { CustomerInformationDialogWrapper } from '../../customer-information-dialog/components/customer-information-dialog.wrapper';
import { TranslationPath } from '../../i18n/model/translation';
import { useI18n } from '../../i18n/services/i18n.service';
import { LoadingIndicatorComponent } from '../../loading/loading-indicator.component';
import { TopSectionComponent } from '../../navigation/components/top-section/top-section.component';
import { VehiclesContainer } from '../../vehicle/components/vehicles/vehicles.container';
import { CustomerInsightsComposable } from './customer-insights/composables/customer-insights.composable';
import { CustomerInsightsComponent } from './customer-insights/customer-insights.component';
import { ProfileSyncComponent } from './profile-sync/profile-sync.component';
import { ProfileComponent } from './profile.component';
import { TabOverviewComponent } from './tab-overview/tab-overview.component';

@Component({
  selector: 'cdf-profile-page',
  providers: [ConsentProvider, GcdmLanguageProvider, ConsentComposable],
  imports: [
    TopSectionComponent,
    ProfileComponent,
    ProfileSyncComponent,
    DsTabsModule,
    DsIconModule,
    LoadingIndicatorComponent,
    TabOverviewComponent,
    CustomerInsightsComponent,
    ConsentBoxesComponent,
    CustomerInformationDialogWrapper,
    VehiclesContainer,
  ],
  template: `
    <cdf-top-section>
      <div topSectionTopRight>
        @if (customer()) {
          <cdf-profile-sync
            [inSync]="isCustomerSyncedToEso()"
            [customer]="customer()"
            [readonly]="readonlyService.readonly()"
            (reloadPage)="loadCustomer(accountNumber())"
          />
        }
      </div>
      <cdf-profile
        [(successfullyInitializedIdCreation)]="successfullyInitializedIdCreation"
        [customer]="customer()"
        [loading]="loading()"
        [connectionError]="connectionError()"
        [readonly]="readonlyService.readonly()"
        (editCustomer)="isEditCustomerInformationModalOpen.set(true)"
      />
    </cdf-top-section>

    @if (customer(); as customer) {
      <cdf-customer-information-dialog-wrapper
        [(isModalOpen)]="isEditCustomerInformationModalOpen"
        [(saving)]="savingEditedCustomer"
        [customer]="customer"
        [editingIdentityDisabled]="true"
        [editingEmailDisabled]="!!customer?.bmwMiniId"
        (saveChanges)="updateCustomer($event)"
      />

      <ds-tabs [(activeTabId)]="activeTabId">
        <ds-tabs-label icon="apps" for="overview">{{ i18n('overview') }}</ds-tabs-label>
        <ds-tabs-content id="overview">
          <cdf-profile-tab-overview
            (openTab)="activeTabId.set($event)"
            [enableVehicleSelection]="enableVehicleSelection()"
            (selectVehicle)="selectVehicle.emit($event)"
            [customerInsights]="customerInsights"
            [consent]="consent"
            [customer]="customer"
            [activeCountry]="dealerProvider.activeCountry()"
          />
        </ds-tabs-content>

        <ds-tabs-label icon="car_bmw" for="vehicles">{{ i18n('vehicles.heading') }}</ds-tabs-label>
        <ds-tabs-content id="vehicles">
          <cdf-vehicles-container
            [readonly]="readonlyService.readonly()"
            [customer]="customer"
            (customerChange)="this.customer.set($event)"
            [enableVehicleSelection]="enableVehicleSelection()"
            (selectVehicle)="selectVehicle.emit($event)"
          />
        </ds-tabs-content>

        <ds-tabs-label icon="list_checked" for="consents">{{ i18n('consent.header') }}</ds-tabs-label>
        <ds-tabs-content id="consents">
          <cdf-consent-boxes
            [gcid]="gcid()"
            [customerLanguage]="customer.correspondenceLanguage"
            [consent]="consent"
            [readonly]="readonlyService.readonly()"
          />
        </ds-tabs-content>

        @if (customerInsights.enabled()) {
          <ng-container>
            <ds-tabs-label icon="column_chart" for="customerInsights" [disabled]="customerInsights.loading()">
              {{ i18n('customerInsights.header') }}
              @if (customerInsights.loading()) {
                <cdf-loading-indicator noSpacing class="tab-label-loading-indicator" />
              }
            </ds-tabs-label>
            <ds-tabs-content id="customerInsights">
              <cdf-customer-insights
                [insights]="customerInsights.insightViewModel()"
                [loading]="customerInsights.loading()"
                [(brandSelection)]="customerInsights.brandSelection"
              />
            </ds-tabs-content>
          </ng-container>
        }
      </ds-tabs>
    }
  `,
  styleUrl: './profile.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfilePage {
  protected readonly i18n = useI18n();
  protected readonly readonlyService = inject(ReadonlyService);
  protected readonly dealerProvider = inject(DealerProvider);
  private readonly customerUpdateService = inject(CustomerUpdateService);
  private readonly dsToastService = inject(DsToastService);
  private readonly logger = inject(NGXLogger);
  private readonly ngZone = inject(NgZone);
  private readonly customerLoadService = inject(CustomerLoadService);

  /** Where the master data on screen comes from; `undefined` until the first load finished. */
  readonly masterDataSource = signal<CustomerMasterDataSource | undefined>(undefined);
  /** The NSC Database could not be read and Retail CRM's mirror is being shown instead. */
  readonly masterDataDegraded = signal(false);

  readonly accountNumber = input.required<string>();

  readonly activeTabId = signal<ProfilePageTabId | undefined>(undefined);
  readonly loading = signal(false);
  readonly connectionError = signal(false);
  readonly isEditCustomerInformationModalOpen = signal(false);
  readonly successfullyInitializedIdCreation = signal(false);
  readonly customer = signal<Customer | undefined>(undefined);
  readonly savingEditedCustomer = signal(false);
  readonly enableVehicleSelection = input(false, { transform: booleanAttribute });

  readonly selectVehicle = output<string>();

  readonly gcid = computed(() => this.customer()?.gcid);

  readonly customerInsights = inject(CustomerInsightsComposable).use(this.gcid);
  readonly consent = inject(ConsentComposable).use(this.gcid, this.customer);

  private toastId = 'PROFILE_PAGE_TOAST';

  readonly isCustomerSyncedToEso = computed(() => this.customer()?.inSyncWithDirectSales ?? false);

  private readonly customerEffect = effect((onCleanup) => {
    const abortController = new AbortController();
    onCleanup(() => abortController.abort('Customer or accountNumber changed, abort loading customer'));
    this.loadCustomer(this.accountNumber(), abortController.signal).catch((e) => {
      this.connectionError.set(true);
      this.logger.error('#CDFTF0109 Could not load customer details', e);
    });
  });

  protected async loadCustomer(accountNumber: string, abortSignal?: AbortSignal) {
    this.connectionError.set(false);
    this.loading.set(true);
    let result: CustomerLoadResult | undefined;
    try {
      // Synced customers are read from the NSC Database, local ones from Retail CRM (CUSTAPP-613).
      result = await this.customerLoadService.loadCustomer(accountNumber);
    } finally {
      if (!abortSignal?.aborted) {
        this.ngZone.run(() => {
          this.loading.set(false);
        });
      }
    }
    if (!abortSignal?.aborted && result) {
      const loaded = result;
      this.ngZone.run(() => {
        this.customer.set(loaded.customer);
        this.masterDataSource.set(loaded.source);
        this.masterDataDegraded.set(loaded.degraded);
      });
    }
  }

  protected async updateCustomer(customer: Customer) {
    this.dsToastService.popToast(this.toastId);
    this.savingEditedCustomer.set(true);

    // "loading" is intentionally not set to "true", as this is already done in customer-information-dialog.container

    try {
      // Synced customers are written to the NSC Database, local ones to Retail CRM (CUSTAPP-613).
      const result = await this.customerUpdateService.updateCustomer(customer);

      if (result.status === 'FAILED') {
        this.showUpdateCustomerFailure(this.toastId, result.reason);
        return;
      }

      this.isEditCustomerInformationModalOpen.set(false);
      this.showUpdateCustomerSuccess(this.toastId);

      // Reload from the system the data was just written to, so the user sees their own edit
      // rather than Retail CRM's asynchronous mirror of it (CUSTAPP-613).
      this.loadCustomer(this.accountNumber()).catch(() => {
        this.connectionError.set(true);
      });
    } catch (error) {
      // CustomerUpdateService reports failures as a result; anything thrown is a defect (e.g. the
      // mapper's missing-name guard) and must still not leave the dialog spinning.
      this.logger.error('#CDFTF0283 Unexpected error while saving the edited customer', error);
      this.showUpdateCustomerFailure(this.toastId, 'UNKNOWN');
    } finally {
      this.loading.set(false);
      this.savingEditedCustomer.set(false);
    }
  }

  private showUpdateCustomerSuccess(toastId: string) {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('customerInformationDialog.updateSuccessHeader'),
      toastText: this.i18n('customerInformationDialog.updateSuccessBody'),
      id: toastId,
      tone: 'positive',
    });
  }

  private showUpdateCustomerFailure(toastId: string, reason: CustomerUpdateFailureReason) {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('customerInformationDialog.updateFailureHeader'),
      toastText: this.i18n(FAILURE_MESSAGE_KEYS[reason]),
      id: toastId,
      tone: 'critical',
    });
  }
}

/** Only the reasons the user can act on get their own message; everything else keeps the generic
 * one, so a new backend error type never renders an empty toast. */
const FAILURE_MESSAGE_KEYS: Record<CustomerUpdateFailureReason, TranslationPath> = {
  CONFLICT: 'customerInformationDialog.updateConflictBody',
  REJECTED: 'customerInformationDialog.updateRejectedBody',
  NOT_FOUND: 'customerInformationDialog.updateNotFoundBody',
  TYPE_NOT_SUPPORTED: 'customerInformationDialog.updateFailureBody',
  UNKNOWN: 'customerInformationDialog.updateFailureBody',
};

export type ProfilePageTabId = 'overview' | 'vehicles' | 'consents' | 'customerInsights';
